/**
 * Screen-local fetch module for the Co-op source-mix panel (epic 091 D12, task
 * 05): `GET /admin/reports/coop-source-mix?campaignId=<id>`, no dates, so the
 * server answers with its default range (the 14 ET days ending today). Outside
 * `lib/api.ts` (ADR-010 A2 b), the same pattern as `reports/daily-spend-api.ts`.
 *
 * The DTO types mirror, by hand and under the same names, the api-local report
 * DTOs in `packages/api/src/modules/reporting/coop-source-mix.service.ts`
 * (ADR-010 A3: the SPA mirrors api-local DTOs; `zod` is not a dependency of
 * this package). Change one side, change the other.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const ADMIN_PREFIX = '/admin';

/** A lead's source (epic 091 D2, D12): `facebook` = site 101, `cto` = own campaign CTO Email, else `other`. */
export type SourceKey = 'facebook' | 'cto' | 'other';

/** One source's numbers on one campaign. */
export interface SourceCounts {
  /** Distinct leads that reached the partner (a pre-ping, or a send that left the platform). */
  offered: number;
  /** The daily cap's count: attributed accepted sends (+ duplicates unless Skip Duplicates). */
  sold: number;
  /** round2(sold x the campaign's **current** price) - epic 091 D14. */
  earned: number;
}

/** One ET day of the range. */
export interface CoopSourceMixDay {
  /** yyyy-MM-dd in the report's timezone - every day in [from, to], ascending. */
  date: string;
  sources: Record<SourceKey, SourceCounts>;
  /** Sum of the sources' sold - the day's daily-cap count. */
  soldTotal: number;
  /** round2(sum of the sources' earned). */
  earnedTotal: number;
  /** dailyCap != null && soldTotal >= dailyCap. */
  capHit: boolean;
  /** Facebook leads the campaign skipped, by reason, and how many of them sold to another campaign. */
  facebookTurnedAway: { byShare: number; bySiteBlock: number; soldElsewhere: number };
}

/** Response of GET /admin/reports/coop-source-mix (epic 091 D12). */
export interface CoopSourceMixResponse {
  campaign: {
    id: string;
    name: string;
    advertiserName: string;
    dailyCap: number | null;
    maxFacebookSharePct: number | null;
    /** The **current** price every `earned` is computed at (D14). */
    pricePerLead: number | null;
  };
  from: string;
  to: string;
  timezone: string;
  days: CoopSourceMixDay[];
  totals: {
    campaign: Record<SourceKey, SourceCounts>;
    /** Every campaign of the advertiser, this one included - no money (prices differ). */
    advertiser: Record<SourceKey, Omit<SourceCounts, 'earned'>>;
  };
}

interface CoopSourceMixEnvelope {
  data: CoopSourceMixResponse;
}

/** Fetch one campaign's source mix over the server's default range. Non-2xx throws. */
export async function getCoopSourceMix(campaignId: string): Promise<CoopSourceMixResponse> {
  const query = new URLSearchParams({ campaignId });
  // HF-147: same options as lib/api.ts apiFetch — without credentials the
  // session cookie is not sent cross-origin (split-host stands 401), and the
  // admin session guard needs the marker header on mutating calls.
  const response = await fetch(
    `${API_BASE_URL}${ADMIN_PREFIX}/reports/coop-source-mix?${query.toString()}`,
    {
      credentials: 'include',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }
  );
  if (!response.ok) {
    throw new Error(`Source mix failed: ${response.status} ${response.statusText}`);
  }
  const envelope: CoopSourceMixEnvelope = await response.json();
  return envelope.data;
}
